import { Dispatch, PropsWithChildren, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContext } from 'react';
import type { SearchResult } from '@/types/dto/SearchResult';
import { useAuthContext } from '@/context/AuthContext.tsx';
import useSearchHistory from '@/api/chat/useSearchHistory.ts';
import usePinnedItems from '@/api/chat/usePinnedItems.ts';
import { useOptimisticState } from '@/hooks/useOptimisticState.ts';
import usePinItem from '@/api/chat/usePinItem.ts';
import { UserContext } from '@/models/UserContext';
import useUserContext from '@/api/chat/useUserContext.ts';
import useChatWithBot from '@/api/chat/useChatWithBot.ts';
import useChatMessages from '@/api/chat/useChatMessages.ts';
import { ChatMessage } from '@/models/ChatMessage';
import { queryClient } from '@/main.tsx';
import { RecallPayload } from '@/types/dto/RecallPayload';
import dayjs from 'dayjs';

export type PartialUserContext = Partial<Omit<UserContext, 'id'>>;

type PinnedItemWithTimestamp = {
  id: string;
  timestamp: number;
};

type ChatMessagesStore = Record<string, ChatMessage[]>;

interface SearchContextProps {
  pinnedItemIdsWithTimestamp: PinnedItemWithTimestamp[];
  pinnedItems: SearchResult[];
  togglePinItem: (itemId: string) => void;
  storageConsent: boolean;
  addSearchResult: (result: SearchResult) => void;
  searchHistory: SearchResult[];
  isLoadingSearchHistory: boolean;
  errorSearchHistory: Error | null;
  isLoadingPinnedItems: boolean;
  errorPinnedItems: Error | null;
  isLoadingPinItem: boolean;
  errorPinItem: Error | null;
  activeChatId: string | null;
  setActiveChatId: Dispatch<SetStateAction<string | null>>;
  isLoadingUserContext: boolean;
  localUserContext: PartialUserContext | null;
  handleSetLocalUserContext: (userContext?: PartialUserContext) => void;
  chatWithBot: ReturnType<typeof useChatWithBot>['mutateAsync'];
  isErrorChatWithBot: boolean;
  errorChatWithBot: Error | null;
  isLoadingChatWithBot: boolean;
  chatMessages: ChatMessage[] | undefined;
  isLoadingChatMessages: boolean;
  isErrorChatMessages: boolean;
  errorChatMessages: Error | null;
  localChatMessagesStore: ChatMessagesStore;
  setLocalChatMessagesStore: Dispatch<SetStateAction<ChatMessagesStore>>;
  recall: (recallPayload: RecallPayload, callback?: (() => void) | undefined) => Promise<void>;
}

const SearchContext = createContext<SearchContextProps>(undefined!);

export const SearchProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuthContext();
  const storageConsent = !!user?.consents?.storage;
  const [localChatMessagesStore, setLocalChatMessagesStore] = useState<ChatMessagesStore>({});

  const pinnedItemsFetched = useRef(false);

  const { mutateAsync: pinItem, isPending: isLoadingPinItem, error: errorPinItem } = usePinItem();

  const {
    data: serverSearchHistory,
    isLoading: isLoadingSearchHistory,
    error: errorSearchHistory,
  } = useSearchHistory(storageConsent);

  const {
    data: serverPinnedItems,
    isLoading: isLoadingPinnedItems,
    error: errorPinnedItems,
    isFetched: isFetchedPinnedItems,
  } = usePinnedItems(storageConsent);

  const {
    mutateAsync: chatWithBot,
    isError: isErrorChatWithBot,
    error: errorChatWithBot,
    isPending: isLoadingChatWithBot,
  } = useChatWithBot();

  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [localSearchResults, setLocalSearchResults] = useState<SearchResult[]>([]);
  const [pinnedItemIdsWithTimestamp, setPinnedItemsIdsWithTimestamp] = useOptimisticState<PinnedItemWithTimestamp[]>(
    []
  );
  const [localUserContexts, setLocalUserContexts] = useState<Record<string, PartialUserContext>>({});

  const {
    data: chatMessages,
    isLoading: isLoadingChatMessages,
    isError: isErrorChatMessages,
    error: errorChatMessages,
  } = useChatMessages(!!activeChatId, {
    chatId: activeChatId,
  });

  const {
    data: userContext,
    isLoading: isLoadingUserContext,
    error: errorUserContext,
  } = useUserContext(storageConsent ? activeChatId : undefined);

  const localUserContext = activeChatId ? (localUserContexts[activeChatId] ?? null) : null;

  const handleSetLocalUserContext = useCallback(
    (userContext?: PartialUserContext) => {
      if (!activeChatId || !userContext) return;

      setLocalUserContexts((prevState) => {
        const currentContexts = { ...prevState };
        if (userContext) {
          currentContexts[activeChatId] = userContext;
        }
        return currentContexts;
      });
    },
    [activeChatId]
  );

  useEffect(() => {
    if (isLoadingUserContext) return;

    if (errorUserContext) {
      console.error('Error while fetching user context:', errorUserContext);
    }

    handleSetLocalUserContext(userContext);
  }, [isLoadingUserContext, userContext, errorUserContext, activeChatId, handleSetLocalUserContext]);

  const searchHistory = useMemo(
    () =>
      [...(serverSearchHistory ?? []), ...localSearchResults].sort((a, b) => {
        return b.timestamp - a.timestamp;
      }),
    [localSearchResults, serverSearchHistory]
  );

  const pinnedItems = useMemo(() => {
    return (
      pinnedItemIdsWithTimestamp
        .map(({ id, timestamp }) => {
          const item = searchHistory.find((item) => item.id === id);
          if (item) {
            item.timestamp = timestamp;
            return item;
          }
          return undefined;
        })
        .filter((item): item is SearchResult => !!item) ?? []
    );
  }, [pinnedItemIdsWithTimestamp, searchHistory]);

  const addSearchResult = useCallback(
    (result: SearchResult) => {
      setLocalSearchResults((prevResults) => {
        const exists = prevResults.find((r) => r.id === result.id);
        if (exists) {
          return prevResults;
        }
        return [result, ...prevResults];
      });
    },
    [setLocalSearchResults]
  );

  const togglePinItem = useCallback(
    (itemId: string) => {
      if (isLoadingPinItem) return;

      setPinnedItemsIdsWithTimestamp(
        (prevPinnedItems) => {
          if (prevPinnedItems.map((item) => item.id).includes(itemId)) {
            return prevPinnedItems.filter((elem) => elem.id !== itemId);
          } else {
            return [
              {
                id: itemId,
                timestamp: dayjs().unix(),
              },
              ...prevPinnedItems,
            ];
          }
        },
        storageConsent ? () => pinItem({ search_id: itemId }) : () => Promise.resolve()
      );
    },
    [setPinnedItemsIdsWithTimestamp, storageConsent, pinItem, isLoadingPinItem]
  );

  const recall = useCallback(
    async (recallPayload: RecallPayload, callback?: (() => void) | undefined) => {
      if (!activeChatId) return;

      const response = await chatWithBot({
        message: null,
        chat_id: activeChatId,
        user_context: localUserContext,
        recall_payload: recallPayload,
      });

      await queryClient.invalidateQueries({ queryKey: ['user'] });

      response.map((chatMessage) => {
        if (!chatMessage.meta?.search_result) return;
        addSearchResult(chatMessage.meta.search_result);
      });

      setLocalChatMessagesStore((prev) => {
        const existingMessages = prev[activeChatId] || [];
        return {
          ...prev,
          [activeChatId]: [...existingMessages, ...response],
        };
      });
      if (callback) {
        requestAnimationFrame(callback);
      }
    },
    [activeChatId, addSearchResult, localUserContext]
  );

  // Set initial pinned items from server
  useEffect(() => {
    if (isLoadingPinnedItems || !serverPinnedItems || pinnedItemsFetched.current) return;

    if (isFetchedPinnedItems) {
      setPinnedItemsIdsWithTimestamp(
        serverPinnedItems.map((item) => ({
          id: item.search_id,
          timestamp: item.timestamp,
        })),
        () => Promise.resolve()
      );
      pinnedItemsFetched.current = true;
    }
  }, [serverPinnedItems, isLoadingPinnedItems, setPinnedItemsIdsWithTimestamp, isFetchedPinnedItems]);

  return (
    <SearchContext.Provider
      value={{
        pinnedItemIdsWithTimestamp,
        pinnedItems,
        togglePinItem,
        storageConsent,
        addSearchResult,
        searchHistory,
        isLoadingSearchHistory,
        errorSearchHistory,
        isLoadingPinnedItems,
        errorPinnedItems,
        isLoadingPinItem,
        errorPinItem,
        activeChatId,
        setActiveChatId,
        isLoadingUserContext,
        localUserContext,
        handleSetLocalUserContext,
        chatWithBot,
        isErrorChatWithBot,
        errorChatWithBot,
        isLoadingChatWithBot,
        chatMessages,
        isLoadingChatMessages,
        isErrorChatMessages,
        errorChatMessages,
        localChatMessagesStore,
        setLocalChatMessagesStore,
        recall,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export function useSearchContext(): SearchContextProps {
  const context = useContext(SearchContext);

  if (typeof context === 'undefined') {
    throw new Error('useSearchContext should be used within the SearchContext provider!');
  }

  return context;
}
